import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Barbell, ArrowRight, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1676655079738-af54dfd6318e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxhdGhsZXRlJTIwdHJhaW5pbmclMjBkYXJrfGVufDB8fHx8MTc4MjUwOTM0MHww&ixlib=rb-4.1.0&q=85";

const demos = [
  { role: "Coach", email: "coach.carter@athletehub.com" },
  { role: "Teacher", email: "teacher.morgan@athletehub.com" },
  { role: "Athlete", email: "athlete@athletehub.com" },
  { role: "Parent", email: "parent@athletehub.com" },
];

export default function AuthPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back.");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]">
      <div className="flex-1 flex flex-col justify-center px-6 md:px-16 lg:px-24 py-12">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <Barbell size={28} weight="fill" color="#FF3B30" />
            <span className="font-head text-2xl font-extrabold uppercase tracking-tight">Athlete<span className="text-[#FF3B30]">Hub</span></span>
          </Link>
          <h1 className="font-head text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none mb-2">Sign in</h1>
          <p className="text-[#A3A3A3] mb-10">Access your personalized hub.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Email</label>
              <input name="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="auth-email-input"
                className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-4 placeholder-gray-600 outline-none transition-colors" placeholder="you@athletehub.com" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Password</label>
              <input name="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="auth-password-input"
                className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-4 placeholder-gray-600 outline-none transition-colors" placeholder="••••••••" />
              <div className="text-right mt-2">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#737373] hover:text-[#FF3B30] uppercase tracking-widest font-semibold transition-colors">Forgot password?</Link>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="auth-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#FF5C53] disabled:opacity-60 text-white font-bold uppercase tracking-widest px-8 py-4 rounded-sm transition-colors">
              {loading ? <Spinner size={20} className="animate-spin" /> : <>Sign in <ArrowRight size={18} weight="bold" /></>}
            </button>
          </form>

          <div className="mt-10 border border-white/10 rounded-sm p-4 bg-[#141414]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-semibold mb-3">Demo accounts · password: demo1234</div>
            <div className="space-y-1.5">
              {demos.map((d) => (
                <button key={d.email} onClick={() => setForm({ email: d.email, password: "demo1234" })} data-testid={`demo-${d.role.toLowerCase().replace(/\s/g, "-")}`}
                  className="w-full flex items-center justify-between text-left text-xs hover:bg-white/5 rounded-sm px-2 py-1.5 transition-colors">
                  <span className="text-[#FF3B30] font-semibold uppercase tracking-wider">{d.role}</span>
                  <span className="text-[#A3A3A3] font-mono">{d.email}</span>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#737373] mt-3">New accounts are created by your Athletic Director.</p>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative w-[45%]">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <h2 className="font-head text-4xl font-black uppercase tracking-tighter leading-none mb-3">One team. One hub.</h2>
          <p className="text-[#A3A3A3] max-w-sm">Connecting directors, coaches, teachers, athletes and parents in one place.</p>
        </div>
      </div>
    </div>
  );
}
